import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { HANDLE_ERROR_AND_THROW } from '@helios/utils'
import { Observable, of } from 'rxjs'
import { catchError, map } from 'rxjs/operators'
import { categoryOfExisting } from '../edit-transaction/transaction-category-wire'

/**
 * The flex-transactions pass-through added by helios PVAL-26492, which relays
 * recon-portfolio-service's `POST /api/v1/trades/get` unreshaped.
 *
 * With `<base href="/helios/">` this resolves to
 * `/helios/helios-flex-transactions/transactions/trades/get` — the same host, and so the same
 * LOCAL-ONLY `proxy.conf.beta.json` entry, as the dialog-metadata and query endpoints.
 */
export const TRADE_GET_URL = `helios-flex-transactions/transactions/trades/get`

/**
 * The `category` a trade READ names, and the reason this is not simply the tran category key.
 *
 * `category` becomes RPS's `details.assetClass`, and `EnfusionClient.getTrades` rejects every
 * `TradeAssetClass` except `LISTED` with a 400: absent reads `/enfnapi/api/trade/common`, `LISTED`
 * reads `/enfnapi/api/trade/listed/bulk`, and those are the only two trade reads that exist
 * (PVAL-26579). Sending `FX_SWAP` because a row resolved to that tran category would earn a
 * rejection, not a different view — so anything that is not LISTED omits the key.
 *
 * The rule is `categoryOfExisting` (`transaction-category-wire.ts`, PVAL-26440) — the same one an
 * Edit save names — with `GL_ENTRY` excluded here: a journal entry is read through
 * `gl/journalentries/get`, never `trades/get`, so it is not a value this endpoint has ever been sent.
 */
export const tradeReadCategory = (
  tranCategory?: string,
): string | undefined => {
  const category = categoryOfExisting(tranCategory)
  return category === 'GL_ENTRY' ? undefined : category
}

/**
 * The request body `POST api/transactions/trades/get` binds (helios `TradeGetApiRequest`).
 *
 * There is no `clientId` (helios PVAL-26693): RPS maps a client to a PO, so a caller-supplied one
 * could read another client's trades. helios derives the client from [accountId], which must be an
 * account of the caller's session (`helios-session-id`, attached by the app's `SessionInterceptor`),
 * otherwise it answers a 400 `accountNotInSession`.
 */
export interface TradeGetRequest {
  accountId: number
  tradeIds: string[]
  category?: string
}

/** RPS relays Enfusion's document verbatim, so nothing here models its shape. */
export type TradeDocument = Record<string, unknown>

/** Requested trade id → the trade Enfusion answered for it. Absent means it resolved to nothing. */
export type TradesById = ReadonlyMap<string, TradeDocument>

/**
 * What a read answered: the trades, and the failure if there was one.
 *
 * **The error is RETURNED, not thrown.** A failed read must not take the dialog down with it — the
 * grid-projected values are still worth showing — but it must not vanish either, which is what a
 * bare `catchError` swallowing to an empty map used to do. The dialogs render it in
 * `hs-dialog-error-details`, the same box the Create/Edit Transaction modal shows below Notes.
 *
 * `error` carries the HTTP error's own body where there is one, because that is RPS's rejection
 * reason relayed verbatim by helios (“no trades/get read for this assetClass…”) and is the only
 * text that says what actually went wrong.
 */
export interface TradeReadResult {
  trades: TradesById
  error: unknown | null
}

/**
 * Reads whole trades from Enfusion, through helios, to prefill the Edit Transaction dialog.
 *
 * The grid row carries only the columns the workbench asked for; the dialog needs every key the
 * metadata document declares. This is where the rest comes from.
 *
 * **Bulk is the shape, not an extra.** The endpoint takes `tradeIds` and answers an array, and the
 * bulk dialog opens over a multi-row selection — so one call serves a selection of any size and
 * the single dialog is simply the one-element case. A per-row fetch would be N round trips for the
 * one screen that always has N rows.
 */
@Injectable()
export class HttpTradeReadService {
  constructor(private readonly http: HttpClient) {}

  /**
   * Trades for every id asked for, keyed by the id as it was REQUESTED, read for the client that
   * owns [accountId] — the raw account id the dialog is open on, which helios checks against the
   * session and derives the client from.
   *
   * Never throws and never fails a dialog: a rejected or empty read answers an empty map, leaving
   * every row on the values it already had — strictly better than a blank modal. Logged rather than
   * swallowed, because "prefill did nothing" and "these trades have no detail" look identical on
   * screen.
   */
  loadMany(
    tradeIds: readonly string[],
    accountId: string,
    tranCategory?: string,
  ): Observable<TradeReadResult> {
    const ids = [...new Set(tradeIds.map(id => id.trim()).filter(Boolean))]
    if (!ids.length) return of({ trades: EMPTY_TRADES, error: null })

    const category = tradeReadCategory(tranCategory)
    const body: TradeGetRequest = {
      accountId: Number(accountId),
      tradeIds: ids,
      ...(category ? { category } : {}),
    }

    return this.http
      .post<unknown>(TRADE_GET_URL, body, {
        // Without this the app's global `ErrorInterceptor` returns EMPTY on every HTTP error: the
        // observable COMPLETES WITHOUT EMITTING, so `catchError` never runs, the subscriber's
        // `next` never fires, and a dialog waiting on the read sits on its spinner forever with
        // nothing to show. The interceptor still reports the error centrally; this only asks it to
        // rethrow afterwards so the caller can react too. Same opt-in `CommandService` uses.
        context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
      })
      .pipe(
        map(document => readResult(document, ids)),
        catchError((error: unknown) => {
          // 400 = helios refused it (no accountId, no ids, an account outside the session:
          // `accountNotInSession`) or RPS rejected it (unknown category, bad id), each with its own
          // body as the entity; 401 = recon:Access; anything else surfaced as a helios 500.
          console.error(
            `[transactions-2] trade read failed for ${ids.length} trade id(s); the dialog keeps ` +
              `its grid-projected values`,
            error,
          )
          return of({ trades: EMPTY_TRADES, error: detailOf(error) })
        }),
      )
  }

  /** The one-trade case, for the single Edit dialog. */
  load(
    tradeId: string,
    accountId: string,
    tranCategory?: string,
  ): Observable<TradeReadResult> {
    return this.loadMany([tradeId], accountId, tranCategory)
  }
}

const EMPTY_TRADES: TradesById = new Map<string, TradeDocument>()

/**
 * The part of an HTTP failure worth showing.
 *
 * `HttpErrorResponse.error` is the upstream BODY — for this endpoint that is RPS's own rejection,
 * relayed verbatim by helios, which is the only part that says what went wrong. The wrapper carries
 * Angular's generic “Http failure response for …: 400 Bad Request”, which names the status and
 * nothing else. Falls back to the whole error when there is no body (a network failure has none).
 */
export const detailOf = (
  error: unknown,
  fallbackUrl = TRADE_GET_URL,
): unknown => {
  const response = error as {
    status?: number
    statusText?: string
    url?: string
    message?: string
    error?: unknown
  } | null
  const body = response?.error

  // The BODY alone. It is helios's own `StandardApiError` — errorId, displayMessage, errorMessage —
  // which is the part a reader can act on. The transport wrapper around it (status, url, Angular's
  // "Http failure response for ...") repeats what the Network tab already says and buries the
  // message it is wrapping.
  if (body != null && body !== '') return body

  // No body at all — a network failure, or a response that carried nothing. Synthesised rather than
  // returning the error itself, because `HttpErrorResponse` serialises to `{}` through the `json`
  // pipe the error box renders with: its fields are prototype getters, which `JSON.stringify` does
  // not see. Without this the box would open on an empty object.
  return {
    status: response?.status ?? null,
    statusText: response?.statusText ?? null,
    url: response?.url ?? fallbackUrl,
    message: response?.message ?? String(error),
  }
}

/**
 * A successful response that is not a trade list is a FAILURE, not an empty read.
 *
 * The endpoint answers a JSON array. Anything else — most often the dev proxy falling through to
 * the SPA's `index.html`, which arrives as HTML with a 200 — would otherwise be read as "no trades
 * matched", clearing the loader and showing no error. That is the one outcome with no symptom at
 * all, so it is reported rather than absorbed.
 */
const readResult = (
  document: unknown,
  ids: readonly string[],
): TradeReadResult => {
  const usable =
    Array.isArray(document) ||
    (document != null && typeof document === 'object')
  if (!usable) {
    console.error(
      '[transactions-2] trade read answered something that is not a trade list; treating it as a ' +
        'failure. A proxy falling through to index.html looks exactly like this.',
      document,
    )
    return {
      trades: EMPTY_TRADES,
      error: {
        status: 200,
        statusText: 'OK',
        url: TRADE_GET_URL,
        message:
          'The trade read answered a 200 that is not a trade list. Check the endpoint is routed ' +
          'and not being served the SPA shell.',
        body: typeof document === 'string' ? document.slice(0, 500) : document,
      },
    }
  }
  return { trades: tradesById(document, ids), error: null }
}

/**
 * The key a returned trade carries its own id under.
 *
 * **`id`, not `tradeId`.** The request keys on `tradeIds` and the response answers `id` — the
 * grid's `tradeId` column and this are the same number under two names, and matching on the wrong
 * one silently finds nothing.
 */
const TRADE_ID_KEY = 'id'

/**
 * The answered array, indexed by the id each trade was REQUESTED under.
 *
 * **Matched by id, never taken positionally.** Nothing promises the array is ordered like the
 * request or even the same length — an id that resolves to nothing is simply absent. Indexing by
 * position would put one trade's values into another trade's dialog on any such gap, which is the
 * single failure here worse than not prefilling at all. It is also why the bulk dialog can ask for
 * a whole selection in one call and still be sure which row got what.
 *
 * The one-id, one-trade case still pairs them up whatever the id's spelling, so a numeric `id`
 * answered for a string request prefills rather than silently doing nothing.
 */
export const tradesById = (
  document: unknown,
  requested: readonly string[],
): TradesById => {
  // Tolerated, not expected: beta answers a root array. A bare object is read as a lone trade.
  const entries = Array.isArray(document)
    ? document
    : document != null && typeof document === 'object'
    ? [document]
    : []

  const trades = entries.filter(
    (entry): entry is TradeDocument =>
      entry != null && typeof entry === 'object' && !Array.isArray(entry),
  )

  const found = new Map<string, TradeDocument>()
  for (const id of requested) {
    const match = trades.find(
      trade => String(trade[TRADE_ID_KEY] ?? '').trim() === id,
    )
    if (match) found.set(id, match)
  }

  if (!found.size && requested.length === 1 && trades.length === 1) {
    found.set(requested[0], trades[0])
  }
  return found
}
